import { applyNavigation } from 'flux/actions/NavigationActions';
import {
  OneSignalServiceType,
  getAnonymousId,
} from 'services/OneSignalService';
import { PushNotificationEvent } from 'trackers/businessLayer';
import { PushMessage } from 'types/PushNotification';
import Logger from 'utils/Logger';
import { businessEventSource } from 'utils/analytics';
import { isCordova } from 'utils/platform';

import { ServiceProps } from './Service';

const LOG_PREFIX = '[push]';

interface PushNotificationServiceType {
  startPush: () => Promise<boolean>;
  destroy: () => void;
}

interface PushProviders {
  oneSignalService: OneSignalServiceType;
}

async function handleOpenedNotificationAnalytics(
  notification: PushMessage,
): Promise<void> {
  const anonymousDeviceId = await getAnonymousId();

  businessEventSource.emitSync(
    new PushNotificationEvent({
      action: 'open',
      template_name: notification.templateName,
      language: notification.language,
      link: notification.link,
      notification_id: notification.id,
      anonymous_device_id: anonymousDeviceId,
      target_id: notification.targetId,
      target_type: notification.targetType,
    }),
  );
}

async function handleReceivedNotificationAnalytics(
  notification: PushMessage,
): Promise<void> {
  const anonymousDeviceId = await getAnonymousId();

  businessEventSource.emitSync(
    new PushNotificationEvent({
      action: 'receive',
      template_name: notification.templateName,
      language: notification.language,
      link: notification.link,
      notification_id: notification.id,
      anonymous_device_id: anonymousDeviceId,
      target_id: notification.targetId,
      target_type: notification.targetType,
    }),
  );
}

function tryNavigate(
  fluxActions: ServiceProps['flux']['actions'],
  path: string | undefined,
  navigationType: string,
): boolean {
  if (!path) {
    Logger.log(
      `${LOG_PREFIX} Navigation not attempted for type "${navigationType}" due to empty path.`,
    );
    return false;
  }

  Logger.log(`${LOG_PREFIX} Navigating via ${navigationType}:`, path);
  try {
    const desc = fluxActions.menu.goTo(path);
    applyNavigation(desc);
    Logger.log(`${LOG_PREFIX} Applied menu.goTo(${path}) successfully.`);
    return true;
  } catch (e) {
    Logger.error(`${LOG_PREFIX} Error applying menu.goTo(${path}):`, e);
    return false;
  }
}

function tryHandleSpecificAction(
  fluxActions: ServiceProps['flux']['actions'],
  notification: PushMessage,
): boolean {
  if (notification.type === 'booking' && notification.id) {
    Logger.log(`${LOG_PREFIX} Handling type "booking", ID:`, notification.id);
    const desc = fluxActions.menu.toSelectedSchedule(notification.id);
    applyNavigation(desc);
    return true;
  }

  if (notification.type === 'message' && notification.id) {
    Logger.log(`${LOG_PREFIX} Handling type "message", ID:`, notification.id);
    const desc = fluxActions.menu.toSelectedMessage(notification.id);
    applyNavigation(desc);
    return true;
  }

  if (
    notification.type === 'search' &&
    notification.city &&
    notification.country
  ) {
    Logger.log(
      `${LOG_PREFIX} Handling type "search", City:`,
      notification.city,
      'Country:',
      notification.country,
    );
    const desc = fluxActions.menu.findThingsToDoIn(
      notification.country,
      notification.city,
    );
    applyNavigation(desc);
    return true;
  }

  return false;
}

function handleNotificationNavigation(
  fluxActions: ServiceProps['flux']['actions'],
  notification: PushMessage,
): void {
  if (tryNavigate(fluxActions, notification.link, 'new link')) {
    return;
  }

  if (tryHandleSpecificAction(fluxActions, notification)) {
    return;
  }

  if (
    notification.type === 'url' &&
    tryNavigate(fluxActions, notification.url, "legacy type 'url'")
  ) {
    return;
  }

  Logger.log(
    `${LOG_PREFIX} No specific handler executed for this opened notification.`,
    notification,
  );
}

function createNoOpService(): PushNotificationServiceType {
  return {
    startPush: async () => false,
    destroy: () => {},
  };
}

export function PushNotificationService(
  props: ServiceProps,
  { oneSignalService }: PushProviders,
): PushNotificationServiceType {
  if (!isCordova()) {
    Logger.log(
      `${LOG_PREFIX} Not a Cordova environment. Returning no-op service.`,
    );
    return createNoOpService();
  }

  if (!oneSignalService) {
    Logger.log(
      `${LOG_PREFIX} oneSignalService is not defined. Returning no-op service.`,
    );
    return createNoOpService();
  }

  const unsubscribers: Array<() => void> = [];

  unsubscribers.push(
    oneSignalService.onNotificationOpened((notification) => {
      Logger.log(`${LOG_PREFIX} Notification Opened:`, notification);
      handleOpenedNotificationAnalytics(notification).catch((error) => {
        Logger.error(`${LOG_PREFIX} Analytics error (opened):`, error);
      });
      handleNotificationNavigation(props.flux.actions, notification);
    }),
  );

  unsubscribers.push(
    oneSignalService.onNotificationReceived((notification) => {
      Logger.log(`${LOG_PREFIX} Notification Received:`, notification);
      handleReceivedNotificationAnalytics(notification).catch((error) => {
        Logger.error(`${LOG_PREFIX} Analytics error (received):`, error);
      });
    }),
  );

  const startPush = async (): Promise<boolean> => {
    return oneSignalService.requestPushPermissions();
  };

  const destroy = (): void => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };

  return {
    startPush,
    destroy,
  };
}
